/**
 * Indian GST computation.
 *
 * Place of supply decides the split: same state as the property means CGST+SGST
 * at half the slab each, a different state means IGST at the full slab. Each tax
 * head is computed independently from the taxable value, which is how it is
 * printed on the invoice and reported in GSTR-1.
 */
import { addMoney, allocate, mulQty, pctOf, toNearestRupee } from './money.js';
export const SAC = {
    RESTAURANT: '996331',
    ACCOMMODATION: '996311',
};
/** Accommodation slab keys off the declared tariff per unit per night. */
export const ROOM_TARIFF_THRESHOLD_PAISE = 750_000; // ₹7,500
export const DEFAULT_TAX = {
    restaurantSac: SAC.RESTAURANT,
    accommodationSac: SAC.ACCOMMODATION,
    roomTariffThreshold: ROOM_TARIFF_THRESHOLD_PAISE,
    roomGstBelow: 12,
    roomGstAbove: 18,
};
/**
 * The accommodation slab for a tariff.
 *
 * The boundary is inclusive on the lower side - "at or below 7,500" is the
 * wording of the notification - and the settings argument defaults to what
 * shipped, so a property that has never opened the tax screen bills unchanged.
 */
export const roomGstRate = (tariffPaise, tax = DEFAULT_TAX) => tariffPaise <= tax.roomTariffThreshold ? tax.roomGstBelow : tax.roomGstAbove;
export function taxLine(input) {
    const { unitPrice, quantity, gstRate, discount = 0, interState = false } = input;
    const gross = mulQty(unitPrice, quantity);
    const taxable = Math.max(0, gross - discount);
    if (interState) {
        const igst = pctOf(taxable, gstRate);
        return {
            gross,
            discount,
            taxable,
            gstRate,
            cgst: 0,
            sgst: 0,
            igst,
            tax: igst,
            total: taxable + igst,
        };
    }
    const half = pctOf(taxable, gstRate / 2);
    return {
        gross,
        discount,
        taxable,
        gstRate,
        cgst: half,
        sgst: half,
        igst: 0,
        tax: half * 2,
        total: taxable + half * 2,
    };
}
export function totalise(lines) {
    const subtotal = addMoney(...lines.map((l) => l.taxable));
    const discount = addMoney(...lines.map((l) => l.discount));
    const cgst = addMoney(...lines.map((l) => l.cgst));
    const sgst = addMoney(...lines.map((l) => l.sgst));
    const igst = addMoney(...lines.map((l) => l.igst));
    const beforeRounding = subtotal + cgst + sgst + igst;
    const total = toNearestRupee(beforeRounding);
    return {
        subtotal,
        discount,
        cgst,
        sgst,
        igst,
        tax: cgst + sgst + igst,
        roundOff: total - beforeRounding,
        total,
    };
}
/**
 * Spread a bill-level discount across lines in proportion to their value, so the
 * line taxable values still add up to the discounted subtotal exactly.
 */
export function apportionDiscount(lines, discount) {
    const weights = lines.map((l) => mulQty(l.unitPrice, l.quantity));
    return allocate(discount, weights);
}
/** Slab-wise tax summary printed at the foot of an invoice and filed in GSTR-1. */
export function slabSummary(lines) {
    const map = new Map();
    for (const line of lines) {
        const row = map.get(line.gstRate) ?? {
            gstRate: line.gstRate,
            taxable: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
        };
        row.taxable += line.taxable;
        row.cgst += line.cgst;
        row.sgst += line.sgst;
        row.igst += line.igst;
        map.set(line.gstRate, row);
    }
    return [...map.values()].sort((a, b) => a.gstRate - b.gstRate);
}
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const isValidGstin = (value) => GSTIN_RE.test(value.toUpperCase());
/** First two digits of a GSTIN are the state code - that is the place of supply. */
export const gstinStateCode = (gstin) => isValidGstin(gstin) ? gstin.slice(0, 2) : null;
export const isInterState = (supplierGstin, buyerGstin) => {
    if (!buyerGstin)
        return false;
    const a = gstinStateCode(supplierGstin);
    const b = gstinStateCode(buyerGstin);
    return Boolean(a && b && a !== b);
};
//# sourceMappingURL=gst.js.map